// @ts-strict-ignore
import { useCallback } from 'react';
import { useSnackBar } from '@usealloy/component-library';
import type { SnippetVersion } from '~/dashboard/views/workflows/service/snippets/types';
import { captureExceptionsToSentry } from '~/helpers/exceptionHandler';
import { scrubServiceCredentials } from '../MagicImport/helpers';
import cloneDeep from 'lodash/cloneDeep';
import { regeneratePublicIds } from 'flow/store/engine/regenerateIds';
import { snippetVersionSchema } from 'flow/service/snippets/schema';
import { ZodError } from 'zod';
import { useAppDispatch, useAppSelector } from '~/store/reduxHooks';
import { createSnippetVersion, getUpstreamSubgraphFromAction } from 'flow/service/snippets/snippets';
import { insertSnippetVersionThunk } from 'flow/store/redux/thunks/snippets';
import { useEditorAccess } from 'flow/editor/context';
import { EditorMode } from 'flow/editor/context/constants';
import { selectWorkflowData, selectWorkflowProps } from 'flow/store/redux/selectors';
import { filterPropsUsedByActions } from './helpers';
import type { Action } from 'flow/store/engine/types';


type UseCopyPasteHandlersReturn = {
  handleCopy: () => Promise<void>;
  handlePaste: () => Promise<void>;
};

export function useCopyPasteHandlers(): UseCopyPasteHandlersReturn {
  const workflowData = useAppSelector(selectWorkflowData);
  const workflowProps = useAppSelector(selectWorkflowProps);

  const addSnackBar = useSnackBar();
  const dispatch = useAppDispatch();

  const { setEditorMode, resetEditorMode } = useEditorAccess();

  /**
   * Copies workflow info to the clipboard in JSON format.
   * If an actionId is supplied, copies a subgraph of all workflow data leading up to and including that action.
   */
  const handleCopy = useCallback(async (actionId?: Action['publicId']) => {
    try {
      const data = actionId ? getUpstreamSubgraphFromAction(workflowData, actionId) : workflowData;

      const { actionsWithNoCredentials, impactedServiceActions } = scrubServiceCredentials({
        actions: cloneDeep(data.actions),
        services: workflowProps.services,
      });

      const filteredProps = filterPropsUsedByActions({
        actions: actionsWithNoCredentials,
        thresholds: data.thresholds,
        workflowProps,
      });

      const snippet: SnippetVersion = createSnippetVersion({
        actions: actionsWithNoCredentials,
        thresholds: data.thresholds,
        dataRoutes: data.dataRoutes,
        tags: filteredProps.tags,
        outcomes: filteredProps.outcomes,
        services: filteredProps.services,
        publishedAttributes: filteredProps.publishedAttributes,
        reasonCodeGroups: filteredProps.reasonCodeGroups,
      });

      const remappedSnippet = regeneratePublicIds(snippet);

      await navigator.clipboard.writeText(JSON.stringify(remappedSnippet));

      addSnackBar({
        duration: 5_000,
        title: 'Successfully copied workflow to clipboard.',
        type: 'success',
      });

      if (impactedServiceActions?.length > 0) {
        addSnackBar({
          duration: 5_000,
          title: `The service credentials in the following service nodes are removed in the workflow copied in the clipboard: ${impactedServiceActions.join(', ')}`,
          type: 'success',
        });
      }
    } catch (error) {
      captureExceptionsToSentry(error);
      addSnackBar({
        duration: 2_000,
        title: 'Something went wrong while copying the workflow data.',
        type: 'danger',
      });
    }
  }, [workflowData, workflowProps, addSnackBar]);

  const handlePaste = useCallback(async () => {
    const text = await navigator.clipboard.readText();
    let snippet: SnippetVersion;
    try {
      const object = JSON.parse(text);
      // todo BUILD-3979 - finalize the Snippet schema
      /* @ts-expect-error */
      snippet = snippetVersionSchema.parse(object);
    } catch (err) {
      if (err instanceof ZodError || err instanceof SyntaxError) {
        addSnackBar({
          duration: 2_000,
          title: 'Cannot paste invalid workflow data',
          type: 'danger',
        });
      }
    }
    if (snippet) {
      try {
        setEditorMode(EditorMode.ReadOnly);
        await dispatch(insertSnippetVersionThunk({ snippet }));
        addSnackBar({
          duration: 2_000,
          title: 'Successfully pasted workflow from clipboard.',
          type: 'success',
        });
      } catch (error) {
        resetEditorMode();
        addSnackBar({
          duration: 2_000,
          title: 'Failed to paste workflow. Please try again.',
          type: 'danger',
        });
      }
    }
  }, [dispatch, addSnackBar, setEditorMode, resetEditorMode]);

  return {
    handleCopy,
    handlePaste,
  };
}
